import type { FC, ReactNode } from 'react'

import { useId } from 'react'
import { cn } from '@/lib/utils'

export type FlagCode = 'gb' | 'de' | 'fr' | 'tn'

interface FlagProps {
    code: FlagCode
    className?: string
}

const starPoints = (cx: number, cy: number, outer: number, inner: number, rotation: number): string => {
    const points: Array<string> = []

    for (let index = 0; index < 10; index++) {
        const radius = index % 2 === 0 ? outer : inner
        const angle = ((rotation + index * 36) * Math.PI) / 180

        points.push(`${(cx + radius * Math.cos(angle)).toFixed(2)},${(cy + radius * Math.sin(angle)).toFixed(2)}`)
    }

    return points.join(' ')
}

const TUNISIA_STAR = starPoints(64.5, 40, 10.5, 4.1, 180)

export const Flag: FC<FlagProps> = ({ code, className }): ReactNode => {
    const id = useId().replace(/:/g, '')
    const classes = cn('block h-auto overflow-hidden rounded-[3px] shadow-[0_0_0_1px_rgba(255,255,255,0.18)]', className)

    if (code === 'gb') {
        return (
            <svg viewBox='0 0 60 30' className={classes} aria-hidden='true'>
                <clipPath id={`gb-s-${id}`}>
                    <path d='M0,0 v30 h60 v-30 z' />
                </clipPath>
                <clipPath id={`gb-t-${id}`}>
                    <path d='M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z' />
                </clipPath>
                <g clipPath={`url(#gb-s-${id})`}>
                    <path d='M0,0 v30 h60 v-30 z' fill='#012169' />
                    <path d='M0,0 L60,30 M60,0 L0,30' stroke='#fff' strokeWidth='6' />
                    <path d='M0,0 L60,30 M60,0 L0,30' clipPath={`url(#gb-t-${id})`} stroke='#C8102E' strokeWidth='4' />
                    <path d='M30,0 v30 M0,15 h60' stroke='#fff' strokeWidth='10' />
                    <path d='M30,0 v30 M0,15 h60' stroke='#C8102E' strokeWidth='6' />
                </g>
            </svg>
        )
    }

    if (code === 'de') {
        return (
            <svg viewBox='0 0 5 3' className={classes} aria-hidden='true'>
                <rect width='5' height='1' y='0' fill='#000' />
                <rect width='5' height='1' y='1' fill='#DD0000' />
                <rect width='5' height='1' y='2' fill='#FFCE00' />
            </svg>
        )
    }

    if (code === 'fr') {
        return (
            <svg viewBox='0 0 3 2' className={classes} aria-hidden='true'>
                <rect width='1' height='2' x='0' fill='#002654' />
                <rect width='1' height='2' x='1' fill='#fff' />
                <rect width='1' height='2' x='2' fill='#CE1126' />
            </svg>
        )
    }

    return (
        <svg viewBox='0 0 120 80' className={classes} aria-hidden='true'>
            <rect width='120' height='80' fill='#E70013' />
            <circle cx='60' cy='40' r='20' fill='#fff' />
            <circle cx='60' cy='40' r='15' fill='#E70013' />
            <circle cx='64' cy='40' r='12' fill='#fff' />
            <polygon points={TUNISIA_STAR} fill='#E70013' />
        </svg>
    )
}
